import {
  maybeParseCalendarDate,
  parseZonedDateTime,
} from '@rematter/common/dateTime';
import { InvoiceStatus } from '@rematter/frontend-lib-api-client';

import { InvoiceRecord } from '../types';

import { toMoney } from '~/modules/trade/utils/money';
import { createQueryAtomFactory } from '~/shared/providers/api/createQueryAtomFactory';

export const invoiceDetailQueryAtom = createQueryAtomFactory(
  'finance.invoiceDetail',
  (params: { id: string }) => ({
    invoices: {
      __args: {
        filter: {
          ids: [params.id],
        },
      },
      entries: {
        id: true,
        type: true,
        documentNumber: true,
        documentDate: true,
        dueDate: true,
        status: true,
        notes: true,
        paymentTerm: true,
        amount: {
          amount: true,
          currencyCode: true,
        },
        openAmount: {
          amount: true,
          currencyCode: true,
        },
        lineItems: {
          id: true,
          description: true,
          quantity: true,
          unit: true,
          unitPrice: {
            amount: true,
            currencyCode: true,
          },
          totalPrice: {
            amount: true,
            currencyCode: true,
          },
          itemId: true,
          item: {
            id: true,
            name: true,
          },
          groupName: true,
          sortOrder: true,
          timestamps: {
            createdAt: true,
            updatedAt: true,
          },
        },
        account: {
          id: true,
          name: true,
          type: true,
        },
        facility: {
          id: true,
          facilityName: true,
        },
        payments: {
          id: true,
          documentNumber: true,
          status: true,
          transactionDate: true,
          invoices: {
            invoice: { id: true },
            appliedAmount: {
              amount: true,
              currencyCode: true,
            },
          },
        },
        timestamps: {
          createdAt: true,
          updatedAt: true,
        },
      },
    },
  }),
  {
    select: (data): { invoice: InvoiceRecord | null } => {
      const entry = data.invoices.entries[0];
      if (!entry) {
        return { invoice: null };
      }

      return {
        invoice: {
          id: entry.id,
          type: entry.type,
          documentNumber: entry.documentNumber,
          documentDate: maybeParseCalendarDate(entry.documentDate) ?? null,
          dueDate: maybeParseCalendarDate(entry.dueDate) ?? null,
          status: entry.status as InvoiceStatus,
          notes: entry.notes ?? null,
          paymentTerm: entry.paymentTerm ?? null,
          amount: toMoney(entry.amount),
          openAmount: toMoney(entry.openAmount),
          accountId: entry.account?.id ?? null,
          accountName: entry.account?.name ?? '',
          accountType: entry.account?.type,
          facilityId: entry.facility?.id ?? '',
          facilityName: entry.facility?.facilityName ?? '',
          lineItems: (entry.lineItems ?? []).map((li) => ({
            id: li.id,
            description: li.description,
            quantity: li.quantity,
            unit: li.unit,
            unitPrice: toMoney(li.unitPrice),
            totalPrice: toMoney(li.totalPrice),
            itemId: li.itemId ?? null,
            groupName: li.groupName ?? null,
            sortOrder: li.sortOrder,
          })),
          payments: (entry.payments ?? []).map((p) => {
            const application = p.invoices?.find(
              (i) => i.invoice.id === entry.id,
            );
            return {
              id: p.id,
              documentNumber: p.documentNumber,
              status: p.status,
              transactionDate:
                maybeParseCalendarDate(p.transactionDate) ?? null,
              appliedAmount: application?.appliedAmount
                ? toMoney(application.appliedAmount)
                : {
                    amount: 0,
                    currencyCode: 'USD',
                    __typename: 'Money' as const,
                  },
            };
          }),
          timestamps: {
            createdAt: parseZonedDateTime(entry.timestamps.createdAt),
            updatedAt: parseZonedDateTime(entry.timestamps.updatedAt),
          },
        },
      };
    },
  },
);
